import React, { useEffect, useRef } from "react";

/**
 * Tilt3D — Applique une inclinaison 3D très subtile (max 2°) au hover.
 * Perspective de type "verre" : impression de profondeur premium.
 *
 * Props :
 *   - max : inclinaison max en degrés (défaut 2)
 *   - perspective : distance perspective (défaut 900)
 *   - glare : afficher un glow qui suit la souris (défaut false)
 */
export default function Tilt3D({ children, max = 2, perspective = 900, glare = false, className = "", ...rest }) {
  const ref = useRef(null);
  const glareRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia?.("(hover: none)").matches) return;

    const el = ref.current;
    if (!el) return;

    let rafId = null;
    const state = { rx: 0, ry: 0, tx: 0, ty: 0, targetRx: 0, targetRy: 0, gx: 50, gy: 50 };

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width;   // 0..1
      const py = (e.clientY - rect.top) / rect.height;   // 0..1
      state.targetRy = (px - 0.5) * 2 * max;
      state.targetRx = -(py - 0.5) * 2 * max;
      state.gx = px * 100;
      state.gy = py * 100;
    };
    const onLeave = () => { state.targetRx = 0; state.targetRy = 0; };

    const loop = () => {
      state.rx += (state.targetRx - state.rx) * 0.12;
      state.ry += (state.targetRy - state.ry) * 0.12;
      el.style.transform = `perspective(${perspective}px) rotateX(${state.rx.toFixed(2)}deg) rotateY(${state.ry.toFixed(2)}deg)`;
      if (glareRef.current) {
        glareRef.current.style.background = `radial-gradient(400px circle at ${state.gx}% ${state.gy}%, rgba(124,58,237,0.10), transparent 60%)`;
      }
      rafId = requestAnimationFrame(loop);
    };
    loop();

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [max, perspective]);

  return (
    <div ref={ref} className={`relative ${className}`} style={{ transformStyle: "preserve-3d", willChange: "transform" }} {...rest}>
      {children}
      {glare && (
        <div
          ref={glareRef}
          className="pointer-events-none absolute inset-0 rounded-[inherit]"
          style={{ mixBlendMode: "screen" }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
